//Built-in libraries

//Third party libraries
import {
  BanknotesIcon,
  ChartBarIcon,
  ClipboardDocumentListIcon,
  DocumentTextIcon,
  IdentificationIcon,
  PaperClipIcon,
} from '@heroicons/react/24/outline';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { postApplication } from '@/api/Rover/ApplicationForm';
import SubmitSuccess from '@/components/Applications/SubmitSuccess';
import Button from '@/components/UI/Button';
import { HookForm } from '@/components/UI/HookForm/Form';
//Custom components
import { HookFormProvider } from '@/components/UI/HookForm/Provider';
//Services
import { ApplicationFormProvider, useApplicationFormContext } from '@/features/ApplicationForm/ApplicationFormProvider';

//STP Libraries

import type { IGetApplicationFormInfoServiceRes, IPostApplicationServicePayload } from '@/api/Rover/ApplicationForm/types';
import DefaultApplicantInformation from '../../Components/DefaultApplicantInformation';
import Attachments from './Attachments';
import Information from './BuildingInformation';
//Assets and Miscellaneous
import { initialValues } from './constants';
import Disclosure from './Disclosure';
import EconomicOverview from './EconomicOverview';
import FinancialInformation from './FinancialInformation';
import ProjectOverview from './ProjectOverview';
import { validationSchema } from './validationSchema';

//Component Begin

const steps = [
  {
    name: 'Applicant Information',
    sectionId: 'applicantInformation',
    Icon: IdentificationIcon,
    component: <DefaultApplicantInformation />,
  },
  {
    name: 'Building Information',
    sectionId: 'step1',
    Icon: ClipboardDocumentListIcon,
    component: <Information />,
  },
  {
    name: 'Financial Information',
    sectionId: 'step2',
    Icon: ChartBarIcon,
    component: <FinancialInformation />,
  },
  {
    name: 'Project Overview',
    sectionId: 'step3',
    Icon: DocumentTextIcon,
    component: <ProjectOverview />,
  },
  {
    name: 'Economic Overview',
    sectionId: 'step4',
    Icon: BanknotesIcon,
    component: <EconomicOverview />,
  },
  {
    name: 'Attachments',
    sectionId: 'step5',
    Icon: PaperClipIcon,
    component: <Attachments />,
  },
  {
    name: 'Disclosure',
    sectionId: 'step6',
    Icon: DocumentTextIcon,
    component: <Disclosure />,
  },
];

interface IBFIPApplicationFormProps {
  formData: IGetApplicationFormInfoServiceRes;
}

const ApplicationFormBase = ({ formData }: IBFIPApplicationFormProps) => {
  const [isSubmitSuccess, setIsSubmitSuccess] = useState(false);
  const { currentStep, setCurrentStep, updateData, data } = useApplicationFormContext();

  const isLast = currentStep === steps.length - 1;

  const submitApplication = useMutation((data: IPostApplicationServicePayload) => postApplication({ data }), {
    onSuccess: () => {
      setIsSubmitSuccess(true);
    },
  });

  const onSubmitHandler = (values: Record<string, any>) => {
    if (!isLast) {
      updateData({
        [steps[currentStep].sectionId]: values[steps[currentStep].sectionId],
      });
      setCurrentStep((prev) => prev + 1);
    } else {
      const applicationData: IPostApplicationServicePayload = {
        formId: formData?.data?.id,
        contactName: data.applicantInformation.fullName,
        contactEmail: data.applicantInformation.contactEmail,
        applicationData: { ...data, step6: { ...values.step6 } },
        files: [
          ...(data?.step5?.f1.selectedFiles || []),
          ...(data?.step5?.f2.selectedFiles || []),
          ...(data?.step5?.f3.selectedFiles || []),
          ...(data?.step5?.f4.selectedFiles || []),
          ...(data?.step5?.w9Form.selectedFiles || []),
          ...(data?.step5?.transparencyForm.selectedFiles || []),
          ...(data?.step5?.businessLicense.selectedFiles || []),
        ],
      };
      submitApplication.mutate(applicationData);
    }
  };

  if (isSubmitSuccess) {
    return (
      <SubmitSuccess
        message={
          formData.data.confirmationMessage ??
          'Thank you for your interest in BFIP. You will also receive a confirmation email with confirmation number.'
        }
      />
    );
  }

  return (
    <div className='container mx-auto'>
      <h2>{formData?.data?.name}</h2>
      <div className='flex mb-10'>
        <aside className='w-1/4 py-6 px-2 sm:px-6 bg-white rounded-l-md'>
          <div className='w-full flex justify-center'>
            <img src={formData?.data.logo} alt='Form Logo' className='h-14 xl:h-20 object-scale-down ' />
          </div>
          <nav className='space-y-1 py-5'>
            {steps.map(({ name, Icon }, index) => (
              <button
                key={index}
                className={`${
                  currentStep === index ? 'bg-neutralColor-lightest text-primaryColor-normal' : 'text-neutralColor-normal'
                } group rounded-md px-3 py-2 flex items-center text-sm font-medium w-full`}
              >
                <Icon className='shrink-0 -ml-1 mr-3 h-6 w-6' />
                {name}
              </button>
            ))}
          </nav>
        </aside>
        <div className='w-full bg-gray-50'>
          <HookFormProvider defaultValues={initialValues} mode='all' resolver={yupResolver(validationSchema[currentStep] as any)}>
            <HookForm onSubmit={onSubmitHandler}>
              {steps[currentStep].component}
              {isLast && (
                <div className='flex gap-4 justify-end p-6'>
                  <Button
                    label='Previous'
                    btnStyle='neutral'
                    type='button'
                    disabled={submitApplication.isLoading}
                    onClick={() => setCurrentStep((prev) => prev - 1)}
                  />
                  <Button
                    label={`${submitApplication.isLoading ? 'Submitting...' : 'Submit'}`}
                    btnStyle='primary'
                    type='submit'
                    disabled={submitApplication.isLoading}
                  />
                </div>
              )}
            </HookForm>
          </HookFormProvider>
        </div>
      </div>
    </div>
  );
};

export const BFIPApplicationForm = ({ ...props }: IBFIPApplicationFormProps) => {
  return (
    <ApplicationFormProvider initialValues={initialValues}>
      <ApplicationFormBase {...props} />
    </ApplicationFormProvider>
  );
};
